<script setup lang="ts">
import FinestAddressSearch from '@/components/FinestAddressSearch.vue'
import FinestCard from '@/components/FinestCard.vue'
import FinestInputText from '@/components/FinestInputText.vue'
import FinestSelect from '@/components/FinestSelect.vue'
import PropertyStepErrors from '@/components/portfolio/PropertyStepErrors.vue'
import { missingWhen } from '@/utils/field-state'
import { useI18n } from 'vue-i18n'
import AddressData = App.Data.EstateObject.AddressData
import Bundesland = App.Enums.Bundesland

withDefaults(
  defineProps<{
    states: { value: Bundesland; label: string }[]
    errors: string[]
    showMissing?: boolean
  }>(),
  { showMissing: false },
)

const placeId = defineModel<string | null>('placeId', { default: null })
const street = defineModel<string | null>('street', { default: null })
const houseNumber = defineModel<string | null>('houseNumber', { default: null })
const addressAddition = defineModel<string | null>('addressAddition', { default: null })
const zipCode = defineModel<string | null>('zipCode', { default: null })
const city = defineModel<string | null>('city', { default: null })
const state = defineModel<Bundesland | null>('state', { default: null })

const { t } = useI18n({ useScope: 'local' })

const applyAddress = (address: AddressData): void => {
  placeId.value = address.google_place_id
  houseNumber.value = address.house_number
  zipCode.value = address.zip_code
  city.value = address.city

  if (address.state !== null) {
    state.value = address.state
  }
}
</script>

<template>
  <FinestCard :title="t('title')" icon="pi pi-map-marker">
    <p class="text-ink-2 mb-4 text-sm">{{ t('hint') }}</p>

    <PropertyStepErrors :messages="errors" class="mb-4" />

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-6">
      <div class="sm:col-span-4">
        <label class="field-label">
          {{ t('fields.street') }}<span class="text-danger" aria-hidden="true">*</span>
        </label>
        <FinestAddressSearch
          v-model="street"
          :aria-label="t('fields.street')"
          :state="missingWhen(showMissing && !street)"
          autofocus
          required
          @select="applyAddress" />
      </div>

      <div>
        <label class="field-label">
          {{ t('fields.house_number') }}<span class="text-danger" aria-hidden="true">*</span>
        </label>
        <FinestInputText
          v-model="houseNumber"
          :aria-label="t('fields.house_number')"
          :state="missingWhen(showMissing && !houseNumber)"
          required />
      </div>

      <div>
        <label class="field-label">{{ t('fields.address_addition') }}</label>
        <FinestInputText v-model="addressAddition" :aria-label="t('fields.address_addition')" />
      </div>

      <div class="sm:col-span-2">
        <label class="field-label">
          {{ t('fields.zip_code') }}<span class="text-danger" aria-hidden="true">*</span>
        </label>
        <FinestInputText
          v-model="zipCode"
          :aria-label="t('fields.zip_code')"
          :maxlength="5"
          :state="missingWhen(showMissing && !zipCode)"
          required />
      </div>

      <div class="sm:col-span-4">
        <label class="field-label"> {{ t('fields.city') }}<span class="text-danger" aria-hidden="true">*</span> </label>
        <FinestInputText
          v-model="city"
          :aria-label="t('fields.city')"
          :state="missingWhen(showMissing && !city)"
          required />
      </div>

      <div class="sm:col-span-3">
        <label class="field-label">
          {{ t('fields.state') }}<span class="text-danger" aria-hidden="true">*</span>
        </label>
        <FinestSelect
          v-model="state"
          :options="states"
          :placeholder="t('placeholder')"
          :aria-label="t('fields.state')"
          :state="missingWhen(showMissing && !state)"
          required />
      </div>
    </div>
  </FinestCard>
</template>

<i18n lang="json">
{
  "de": {
    "title": "Lage",
    "hint": "Tippe die Adresse ins Straßen-Feld – wähle einen Vorschlag und Hausnummer, PLZ, Ort und Bundesland füllen sich automatisch.",
    "placeholder": "Bitte wählen …",
    "fields": {
      "street": "Straße",
      "house_number": "Hausnummer",
      "address_addition": "Zusatz",
      "zip_code": "PLZ",
      "city": "Ort",
      "state": "Bundesland"
    }
  },
  "en": {
    "title": "Location",
    "hint": "Type the address into the street field – pick a suggestion and house number, postal code, city and federal state fill in automatically.",
    "placeholder": "Please select …",
    "fields": {
      "street": "Street",
      "house_number": "House number",
      "address_addition": "Addition",
      "zip_code": "Postal code",
      "city": "City",
      "state": "Federal state"
    }
  }
}
</i18n>
